import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { ArrowRight, ArrowsLeftRight, FileText, Receipt, UploadSimple } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard } from '../../../../../../components/glass';
import { useContaCilContas } from '../../../../../../hooks/useContaCilContasBancarias';
import { useContaCilDashboard } from '../../../../../../hooks/useContaCilDashboard';
import { useContaCilResumo } from '../../../../../../hooks/useContaCilLancamentos';
import { fmtValor } from '../../conciliar/shared';
import { ProgressoConciliacao } from '../../conciliar/components/ProgressoConciliacao';
import { MiniKpiRow } from '../../plano-contas/ui';
import { AreaEvolucaoConciliacao } from './charts';

const BASE = '/modulesHome/accountingConciliation/conciliacao-contabil';

export function AbaVisaoSaude({ empresaId }: { empresaId: number }) {
  const navigate = useNavigate();
  const filtro = { empresaId, page: 1, size: 1 };
  const { data: resumoData } = useContaCilResumo(filtro);
  const { data: dashboard } = useContaCilDashboard({
    empresa_id: empresaId,
    ano: new Date().getFullYear(),
  });
  const { data: contas = [] } = useContaCilContas(empresaId);
  const resumo = resumoData?.resumo;

  const kpis = [
    {
      label: '% conciliado',
      valor:
        resumo && resumo.soma_total > 0
          ? `${Math.round((resumo.soma_conciliado / resumo.soma_total) * 100)}%`
          : '—',
      icon: <ArrowsLeftRight size={16} weight="duotone" />,
      accent: '#10B981',
    },
    {
      label: 'A resolver (R$)',
      valor: resumo ? `R$ ${fmtValor(resumo.soma_pendente + resumo.soma_revisao)}` : '—',
      icon: <Receipt size={16} weight="duotone" />,
      accent: '#F59E0B',
    },
    {
      label: 'Lançamentos',
      valor: resumo ? String(resumo.total) : '—',
      icon: <FileText size={16} weight="duotone" />,
      accent: '#6366F1',
    },
    {
      label: 'Contas',
      valor: String(contas.length),
      icon: <ArrowsLeftRight size={16} weight="duotone" />,
      accent: '#8B5CF6',
    },
  ];

  // `withEmpresa=true` -> o atalho carrega ?empresa=X pra pre-popular o
  // select da tela destino. A tela /importar NAO tem pre-selecao global de
  // empresa (cada linha tem um seletor inline pra atribuir o import a_
  // identificar), entao nao carrega o param.
  const atalhos: {
    label: string;
    icon: React.ReactNode;
    path: string;
    withEmpresa: boolean;
  }[] = [
    {
      label: 'Conciliar lançamentos',
      icon: <ArrowsLeftRight size={16} weight="bold" />,
      path: 'conciliar',
      withEmpresa: true,
    },
    {
      label: 'Extratos enviados',
      icon: <Receipt size={16} weight="bold" />,
      path: 'extratos',
      withEmpresa: true,
    },
    {
      label: 'Plano de contas',
      icon: <FileText size={16} weight="bold" />,
      path: 'plano-contas',
      withEmpresa: true,
    },
    {
      label: 'Importar extrato',
      icon: <UploadSimple size={16} weight="bold" />,
      path: 'importar',
      withEmpresa: false,
    },
  ];

  return (
    <Box>
      <MiniKpiRow kpis={kpis} />

      {resumo && <ProgressoConciliacao resumo={resumo} />}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <AreaEvolucaoConciliacao meses={dashboard?.meses_status ?? []} />

        <GlassCard intensity="medium" p={5}>
          <Text
            fontSize="10px"
            fontWeight="700"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.06em"
            mb={3}
          >
            Atalhos da empresa
          </Text>
          <Flex direction="column" gap={2}>
            {atalhos.map(a => (
              <Flex
                key={a.path}
                as="button"
                align="center"
                gap={3}
                w="full"
                textAlign="left"
                px={3}
                py={2.5}
                borderRadius="xl"
                borderWidth="1px"
                borderColor="rgba(15,23,42,0.06)"
                bg="rgba(255,255,255,0.5)"
                _hover={{ borderColor: 'rgba(14,165,233,0.35)', bg: 'rgba(224,242,254,0.4)' }}
                transition="all 0.2s"
                onClick={() =>
                  navigate(
                    a.withEmpresa ? `${BASE}/${a.path}?empresa=${empresaId}` : `${BASE}/${a.path}`,
                  )
                }
              >
                <Box color="#0EA5E9">{a.icon}</Box>
                <Text fontSize="13px" fontWeight="600" color="#0F172A" flex={1}>
                  {a.label}
                </Text>
                <Box color="#CBD5E1">
                  <ArrowRight size={14} weight="bold" />
                </Box>
              </Flex>
            ))}
          </Flex>
        </GlassCard>
      </SimpleGrid>
    </Box>
  );
}
